import { sumBy } from 'lodash';
import Typography from '@mui/material/Typography';
import { styled } from '@mui/material/styles';
import { grey } from '@mui/material/colors';
import { CurrencyCode } from 'further-types/lp/vehicle';
import { numberToCurrencyString } from 'further-ui/utils';
import { IncomeStatementSection } from '../types';

const CustomRowText = styled(Typography)(({ theme }) => ({
  paddingTop: theme.spacing(theme.spacingSizes.xxs),
  paddingBottom: theme.spacing(theme.spacingSizes.xxs),
}));

export function getIncomeOrExpensesCustomRow(
  baseCurrency: CurrencyCode,
  section: IncomeStatementSection,
) {
  return getIncomeStatementCustomRow({
    baseCurrency,
    key: `total-${section.title.toLowerCase()}`,
    label: `Total ${section.title.toLowerCase()}`,
    period1: sumBy(section.items, (item) => item.period1),
    period2: sumBy(section.items, (item) => item.period2),
    period3: sumBy(section.items, (item) => item.period3),
  });
}

export function getRealisedAndUnrealisedGainLossCustomRow(
  baseCurrency: CurrencyCode,
  sections: Array<IncomeStatementSection>,
) {
  const sectionTitle = 'Realised and unrealised gain / (loss) on investments';
  const displayLabel =
    'Net realised and unrealised gain / (loss) on investments';

  return getIncomeStatementCustomRow({
    baseCurrency,
    key: 'net-realised-and-unrealised-gain-loss-on-investments',
    label: displayLabel,
    period1: sectionTotal(sections, sectionTitle, 'period1'),
    period2: sectionTotal(sections, sectionTitle, 'period2'),
    period3: sectionTotal(sections, sectionTitle, 'period3'),
  });
}

export function getOtherRealisedAndUnrealisedFxGainLossCustomRow(
  baseCurrency: CurrencyCode,
  sections: Array<IncomeStatementSection>,
) {
  const sectionTitle = 'Other realised and unrealised FX gain / (loss)';
  const displayLabel = 'Net other realised and unrealised gain / (loss)';

  return getIncomeStatementCustomRow({
    baseCurrency,
    key: 'net-other-realised-and-unrealised-gain-loss',
    label: displayLabel,
    period1: sectionTotal(sections, sectionTitle, 'period1'),
    period2: sectionTotal(sections, sectionTitle, 'period2'),
    period3: sectionTotal(sections, sectionTitle, 'period3'),
  });
}

export function getNetOperatingIncomeLossCustomRow(
  baseCurrency: CurrencyCode,
  sections: Array<IncomeStatementSection>,
) {
  return getIncomeStatementCustomRow({
    baseCurrency,
    key: 'net-operating-income-loss',
    label: 'Net operating income / (loss)',
    period1: calculateNetOperatingIncome(sections, 'period1'),
    period2: calculateNetOperatingIncome(sections, 'period2'),
    period3: calculateNetOperatingIncome(sections, 'period3'),
    fontWeight: 700,
    backgroundColor: grey[100],
  });
}

export function getNetIncomeLossCustomRow(
  baseCurrency: CurrencyCode,
  sections: Array<IncomeStatementSection>,
) {
  return getIncomeStatementCustomRow({
    baseCurrency,
    key: 'net-income-loss',
    label: 'Net income / (loss)',
    period1: calculateNetIncome(sections, 'period1'),
    period2: calculateNetIncome(sections, 'period2'),
    period3: calculateNetIncome(sections, 'period3'),
    fontWeight: 700,
    backgroundColor: grey[100],
  });
}

function getIncomeStatementCustomRow({
  baseCurrency,
  key,
  label,
  period1,
  period2,
  period3,
  fontWeight = undefined,
  backgroundColor = grey[50],
}: {
  baseCurrency: CurrencyCode;
  key: string;
  label: string;
  period1: number;
  period2: number;
  period3: number;
  fontWeight?: number;
  backgroundColor?: string;
}) {
  const formatValue = (value: number) =>
    numberToCurrencyString(value, {
      currency: baseCurrency,
      negativeInBrackets: true,
    });

  return [
    {
      key,
      content: (
        <CustomRowText variant="h6" fontWeight={fontWeight}>
          {label}
        </CustomRowText>
      ),
      backgroundColor,
    },
    {
      key: 'period1',
      content: (
        <CustomRowText variant="h6" fontWeight={fontWeight}>
          {formatValue(period1)}
        </CustomRowText>
      ),
      backgroundColor,
    },
    {
      key: 'period2',
      content: (
        <CustomRowText variant="h6" fontWeight={fontWeight}>
          {formatValue(period2)}
        </CustomRowText>
      ),
      backgroundColor,
    },
    {
      key: 'period3',
      content: (
        <CustomRowText variant="h6" fontWeight={fontWeight}>
          {formatValue(period3)}
        </CustomRowText>
      ),
      backgroundColor,
    },
  ];
}

function sectionTotal(
  sections: Array<{
    title: string;
    items: Array<{ period1: number; period2: number; period3: number }>;
  }>,
  title: string,
  period: 'period1' | 'period2' | 'period3',
) {
  const section = sections.find((s) => s.title === title);
  return section ? sumBy(section.items, (item) => item[period]) : 0;
}

function calculateNetOperatingIncome(
  sections: Array<{
    title: string;
    items: Array<{ period1: number; period2: number; period3: number }>;
  }>,
  period: 'period1' | 'period2' | 'period3',
) {
  return (
    sectionTotal(sections, 'Income', period) -
    sectionTotal(sections, 'Expenses', period)
  );
}

function calculateNetIncome(
  sections: Array<{
    title: string;
    items: Array<{ period1: number; period2: number; period3: number }>;
  }>,
  period: 'period1' | 'period2' | 'period3',
) {
  return (
    calculateNetOperatingIncome(sections, period) +
    sectionTotal(
      sections,
      'Realised and unrealised gain / (loss) on investments',
      period,
    ) +
    sectionTotal(
      sections,
      'Other realised and unrealised FX gain / (loss)',
      period,
    )
  );
}
